/**
 * Shared helpers for content features (no feature-folder coupling).
 */

import { sanitizeToPlainText } from "./sanitize";

export const CONTENT_PREVIEW_LENGTH = 220;

export const formatDate = (dateString, options = {}) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const defaultOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  };
  return date.toLocaleDateString("en-US", defaultOptions);
};

export const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  return formatDate(new Date(timestamp).toISOString(), {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const stripHtml = (html) => sanitizeToPlainText(html);

export const getInitials = (text) => {
  if (!text) return "P";
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "P";
  if (words.length === 1) return words[0][0]?.toUpperCase() || "P";
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

export const getLikesCount = (item) => {
  if (!item) return 0;
  if (typeof item.likesCount === "number") return Math.max(0, item.likesCount);
  if (!item.likes) return 0;
  return Object.keys(item.likes).length;
};

export const getCommentsCount = (item) => {
  if (!item) return 0;
  if (typeof item.commentsCount === "number") return Math.max(0, item.commentsCount);
  if (!item.comments) return 0;
  return Object.keys(item.comments).length;
};

export const hasUserLiked = (item, user) => {
  if (!item || !item.likes || !user) return false;
  if (user.uid && item.likes[user.uid]) return true;
  const userName = user.displayName || user.fullName || user.email;
  return !!(userName && item.likes[userName]);
};

export const getUserName = (user) => {
  if (!user) return "";
  return user.displayName || user.fullName || user.email || "Anonymous";
};

export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/** ~200 words per minute */
export const estimateReadingMinutes = (htmlOrText) => {
  const text = stripHtml(htmlOrText || "");
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
};

/** Milliseconds for `createdAt` stored as a number, ISO string, or Firestore Timestamp. */
export function getContentCreatedMs(item) {
  const t = item?.createdAt;
  if (t == null) return 0;
  if (typeof t?.toDate === "function") return t.toDate().getTime();
  if (typeof t === "number") return t;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

export const normalizeTags = (tags) => {
  if (!Array.isArray(tags)) return [];
  return [...new Set(tags.map((t) => String(t).trim().toLowerCase()).filter(Boolean))];
};
